import type { Entity } from '@/types/entity';
import { getAssertionCandidates } from '@/engine/canonBundle';

const EXCERPT_LIMIT = 600;

export interface PreambleCounts {
  hard: number;
  unchecked: number;
  proposals: number;
}

/**
 * 컨텍스트 프리앰블(재설계 R3, 클라이언트측 브리지) — 현재 비트·수락 전제·검증 요약을
 * 에이전트 지시 앞에 합성한다. ※ P1(에이전트 read 도구가 Dexie 미러를 직접 읽음)이 오면 폐기되는 과도기.
 * 봉인 주의: 본문·수락 전제는 작가가 이미 쓴 현재 비트의 것만 — 미래 part 정보는 넣지 않는다.
 */
/** 커널 홈(K5-f)용 — 프로젝트·검증 상태·승인 대기를 한 줄로(1차 표면이 가장 컨텍스트-빈곤하던 갭의 공짜 픽스). */
export function buildHomePreamble(opts: { projectTitle: string; hard: number; pendingProposals: number }): {
  preamble: string;
  note: string;
} {
  return {
    preamble: [
      '[컨텍스트 — 커널 홈에서 자동 주입]',
      `프로젝트: ${opts.projectTitle}`,
      `검증 상태: 세계와 충돌 ${opts.hard} · 승인 대기 제안 ${opts.pendingProposals}`,
    ].join('\n'),
    note: '홈',
  };
}

/** 글로벌 도크(K2)용 — 현재 화면·선택 엔티티를 컨텍스트로(비트 전용 프리앰블의 일반화). */
export function buildViewPreamble(opts: {
  viewLabel: string;
  entity?: { name: string; type: string; summary?: string } | null;
}): { preamble: string; note: string } {
  const lines = [
    '[컨텍스트 — Loom 화면에서 자동 주입. 작가가 지금 보고 있는 것]',
    `화면: ${opts.viewLabel}`,
  ];
  if (opts.entity) {
    lines.push(`선택: ${opts.entity.name} (${opts.entity.type})${opts.entity.summary ? ` — ${opts.entity.summary}` : ''}`);
  }
  return {
    preamble: lines.join('\n'),
    note: opts.entity ? `${opts.viewLabel} · ${opts.entity.name}` : opts.viewLabel,
  };
}

export function buildBeatPreamble(
  beat: Entity,
  opts: { timeScope?: string; counts?: PreambleCounts | null } = {},
): { preamble: string; note: string } {
  const body = String(beat.fields.bodyDraft ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const excerpt = body.length > EXCERPT_LIMIT ? `${body.slice(0, EXCERPT_LIMIT)}…` : body;
  const assertions = getAssertionCandidates(beat);
  const accepted = assertions.filter((a) => a.status === 'accepted');
  const pending = assertions.filter((a) => a.status === 'pending').length;

  const lines = [
    '[컨텍스트 — Loom 원고에서 자동 주입. 이 비트 기준으로 답하라]',
    `비트: "${beat.name}"${opts.timeScope ? ` (시간축 ${opts.timeScope})` : ''}`,
  ];
  if (excerpt) lines.push(`본문 발췌: ${excerpt}`);
  if (accepted.length > 0) {
    lines.push(`수락된 전제 ${accepted.length}:`);
    for (const a of accepted.slice(0, 12)) lines.push(`- ${a.claim}`);
  }
  if (pending > 0) lines.push(`대기 전제 ${pending}건(작가 미결정)`);
  if (opts.counts) {
    lines.push(`검증 요약: 세계와 충돌 ${opts.counts.hard} · 미검증 ${opts.counts.unchecked} · 수리 제안 ${opts.counts.proposals}`);
  }
  return { preamble: lines.join('\n'), note: `비트 "${beat.name}"` };
}
